/**
 * Runs various assertions, then assigns a score based on the health of the UI
 * APP. 0 = healthy. 10 = fatal. The score from 0-10 is a confidence indicator
 * of a fatal (unrecoverable) problem.
 *
 *
 */

import { getWeasleyAppConfig } from '@/lib/globals/globals';

import type { WeasleyAppConfig } from '@/types';
import type { RootElType } from './health-utils';
import {
  generateSimpleDomTree,
  getRootElDimensions,
  getVisibleRootDepth,
  getVisibleTextCount,
} from './health-utils';

/**
 * Why was the healthcheck called?
 */
export type HealthCheckEventType = 'onload' | 'reactMounted' | '';
type AssertionWeights = WeasleyAppConfig['healthCheck']['ASSERTION_WEIGHTS'];
type HealthCheckAssertionType = keyof AssertionWeights;

export type ScoreCardType = {
  _assertionRules: {
    ASSERTION_WEIGHTS: Partial<AssertionWeights>;
    THRESHOLD_FATAL: number;
    /**
     * tells if config/UCP access is working or it's using a fallback mechanism:-1 means "not found"
     */
    RULE_SOURCE: string;
  };
  /**
   * What event caused the scoreCard to be generated / healtCheck to be run?
   */
  event: string;
  /**
   * What file caused the healthCheck to be run?
   */
  eventSrc: string;
  failedAssertions: HealthCheckAssertionType[];
  score: number;
  scoreReasons: {
    _domReadyState: DocumentReadyState;
    /**
     * height_width
     */
    rootElDim: string;
    visibleRootDepth: number;
    visibleTextCount: number;
    /**
     * Count for how many form fields are present and loaded.
     */
    formFields: number;
    /**
     * Simplified DOM tree represention. Similar to Jade templates.
     */
    rootDomTree: string;
  };
  /**
   * Sends detailed scoreCard to cal/FPTI
   */
  sendScoreCardsToCalAndFPTI: boolean;
  timestamp: number; // FIXME: Change this to time after render... start or parseStart or something...?
};

// Constants are defined via app config in severus/config.json:weasleyAppConfig
const weasleyAppConfig = getWeasleyAppConfig();

const sendSampleScoreCardsToCalAndFPTI =
  weasleyAppConfig.healthCheck?.SEND_SCORECARD_DETAILS_TO_CAL_FPTI ?? false;

/**
 * Assume score > 4 is FATAL.
 * Fallback to very tolerant score. WE'd rather err on the side of passing than failure.
 */
const THRESHOLD_FATAL_FALLBACK = 10;
export const THRESHOLD_FATAL =
  weasleyAppConfig.healthCheck?.THRESHOLD_FATAL ?? THRESHOLD_FATAL_FALLBACK;

/**
 *
 */
const ASSERTION_WEIGHTS = weasleyAppConfig.healthCheck?.ASSERTION_WEIGHTS;

/**
 *
 *
 */
const ASSERTION_THRESHOLDS = weasleyAppConfig.healthCheck?.ASSERTION_THRESHOLDS;
// TODO: Figure out a way to log / notify us
// when the ucp values aren't being pulled in properly on live...
// Maybe Log a Cal warning or error?
const minVisibleTextCountToPass = ASSERTION_THRESHOLDS?.visibleTextCount ?? -1; // if assertion minLevel not set, then automagically pass the assertion

const minRootElDimWidthOrHeightToPass =
  ASSERTION_THRESHOLDS?.rootElWidthOrHeight ?? -1;

const minVisibleRootDepthToPass = ASSERTION_THRESHOLDS?.visibleRootDepth ?? -1;

//   rootDepth: 1,
//     rootElDimensionsZero: 5, // if rootElDimensions are zero major problem
//       visibleTextCount: 5, // if there's no visible text major problem

// assertion weights
// dimension size, text el count, depth.

/**
 * Generates a health score with detailed scorecard for the current page
 * @param rootEl
 * @param event {HealthCheckEventType} What event caused this healthcheck to run?
 * @param eventSrc {string} What file called healtCheck()?
 */
export const generateHealthScore = function (
  rootEl: RootElType,
  event: HealthCheckEventType,
  eventSrc: string,
): ScoreCardType {
  let score = 0;
  const failedAssertions: HealthCheckAssertionType[] = [];

  // gather the data we need to generate a health score
  // Highest weighted tests first, so we can send the first failed assertion to signalFx

  // # score visible text count
  const visibleTextCount = getVisibleTextCount(rootEl);

  if (visibleTextCount < minVisibleTextCountToPass) {
    const weight = ASSERTION_WEIGHTS?.visibleTextCount ?? 0;
    score = score + 1 * weight;
    failedAssertions.push('visibleTextCount');
  }

  // # score rootElDimension size (for blank page we assume zero height)
  const rootElDimensions = getRootElDimensions(rootEl);
  if (
    rootElDimensions.width < minRootElDimWidthOrHeightToPass ||
    rootElDimensions.height < minRootElDimWidthOrHeightToPass
  ) {
    const weight = ASSERTION_WEIGHTS?.rootElDimensionsZero ?? 0;
    score = score + 1 * weight;
    failedAssertions.push('rootElDimensionsZero');
  }

  // # score visible rootEl Dom depth.
  // TODO: Consider counting visible dom nodes instead...
  const visibleRootDepth = getVisibleRootDepth(rootEl);

  if (visibleRootDepth < minVisibleRootDepthToPass) {
    // FIXME: Turn this into a separate function...
    // Unique parts: NAME and weight key name (use the same for both)
    // so really just FAIL_ASSERTION(keyName) OR ADD_SCORE(keyName, failedAssertions)
    // just include it as a sub function in the same scope...
    const weight = ASSERTION_WEIGHTS?.visibleRootDepth ?? 0;
    score = score + 1 * weight;
    failedAssertions.push('visibleRootDepth');
  }

  // consider saving which fields are loaded... and which aren't...
  // YAS. Just by name of the field...
  //
  //
  // Q: check for loading spinner? LATER...

  // LATER: Can we publish a form loaded %? Would take some input from react components...
  // expected vs loading... We could count the svg loading skeleton indicators

  // const rootDepthScore =

  // FIXME: Add tests for this...
  // if we know the config values aren't present, set the score as -1 to show that
  // the score is worthless
  if (!ASSERTION_WEIGHTS || THRESHOLD_FATAL === THRESHOLD_FATAL_FALLBACK) {
    score = -1;
  }

  //
  return {
    _assertionRules: {
      ASSERTION_WEIGHTS: ASSERTION_WEIGHTS ?? {},
      // FIXME: Just attach to the main thing...?
      RULE_SOURCE: weasleyAppConfig.configSource ?? '-1',
      THRESHOLD_FATAL: THRESHOLD_FATAL,
    },
    event,
    eventSrc,
    failedAssertions,
    score,
    scoreReasons: {
      _domReadyState: document.readyState,
      formFields: rootEl?.querySelector('form')?.length ?? -1,
      rootDomTree: generateSimpleDomTree(rootEl),
      rootElDim: rootElDimensions.width + '_' + rootElDimensions.height,
      visibleRootDepth,
      visibleTextCount,
    },
    sendScoreCardsToCalAndFPTI: sendSampleScoreCardsToCalAndFPTI,
    timestamp: Date.now(), // FIXME: Change this to time after render... start or parseStart or something...?
  };
};
